import { createContext, useContext } from 'react';

/**
 * Context and hook for the navigation guard.
 *
 * Split out of `NavigationGuard.tsx` because `react-refresh/only-export-components`
 * fails the lint gate when a component file also exports non-components — the
 * same reason `navIcon.ts` sits beside `AppNavList.tsx`.
 *
 * See NavigationGuard.tsx for why this exists at all rather than using React
 * Router's `useBlocker`.
 */

/**
 * Receives the navigation it is blocking. Return false to mean "I have stashed
 * this and will run it if the user confirms", which keeps the decision and its
 * dialog inside the page that knows what is unsaved.
 */
export type NavigationGuard = (proceed: () => void) => boolean;

export interface NavigationGuardValue {
  /** Registers (or clears, with null) the current guard. */
  registerGuard: (guard: NavigationGuard | null) => void;
  /** Runs `proceed` unless a guard defers it. */
  attemptNavigate: (proceed: () => void) => void;
}

export const NavigationGuardContext = createContext<NavigationGuardValue>({
  registerGuard: () => {},
  // No provider: navigate normally rather than silently swallowing it. A
  // missing guard must never be able to trap someone on a page.
  attemptNavigate: (proceed) => proceed(),
});

export function useNavigationGuard(): NavigationGuardValue {
  return useContext(NavigationGuardContext);
}
